import React, { useState, useEffect } from 'react';
import { Trash2, X } from 'lucide-react';
import { PlanDetail } from '../../types';
import PlanFormFields from './PlanFormFields';
import PlanFeaturesList from './PlanFeaturesList';

interface PlanModalProps {
  onClose: () => void;
  onSave: (plan: PlanDetail) => Promise<void> | void;
  onDelete?: (id: string) => Promise<void> | void;
  initialPlan?: PlanDetail | null;
}

const PlanModal: React.FC<PlanModalProps> = ({ onClose, onSave, onDelete, initialPlan }) => {
  const isEditing = !!initialPlan;
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [tierName, setTierName] = useState(initialPlan?.tier || '');
  const [planId, setPlanId] = useState(initialPlan?.id || '');
  const [isCustomPlanId, setIsCustomPlanId] = useState(!!initialPlan?.id);
  const [price, setPrice] = useState<number | string>(initialPlan?.price ?? 3999);
  const [currency, setCurrency] = useState(initialPlan?.currency || 'INR');
  const [description, setDescription] = useState(initialPlan?.description || '');
  const [features, setFeatures] = useState<string[]>(
    initialPlan?.features && initialPlan.features.length > 0 ? [...initialPlan.features] : ['']
  );
  const [isPopular, setIsPopular] = useState(initialPlan?.isPopular ?? false);

  useEffect(() => {
    if (!isEditing && !isCustomPlanId && tierName) {
      const slug = tierName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      setPlanId(slug ? `plan_${slug}` : '');
    }
  }, [tierName, isEditing, isCustomPlanId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tierName.trim()) return;

    const finalPlanId = planId.trim() || `plan_${Date.now()}`;
    const cleanFeatures = features.map(f => f.trim()).filter(Boolean);

    const planToSave: PlanDetail = {
      id: finalPlanId,
      tier: tierName.trim(),
      price: Math.max(0, Number(price) || 0),
      currency: currency || 'INR',
      description: description.trim(),
      features: cleanFeatures.length > 0 ? cleanFeatures : ['Core workspace access'],
      color: initialPlan?.color || 'bg-slate-100 text-slate-700',
      isActive: initialPlan?.isActive ?? true,
      isPopular,
      aiCredits: initialPlan?.aiCredits ?? 0,
      bulkDiscountRules: initialPlan?.bulkDiscountRules || [],
      meteredRules: initialPlan?.meteredRules || [],
      addons: initialPlan?.addons || [],
    };

    setIsSaving(true);
    try {
      await onSave(planToSave);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!initialPlan || !onDelete) return;
    if (window.confirm(`Are you sure you want to delete "${initialPlan.tier}"?`)) {
      setIsDeleting(true);
      try {
        await onDelete(initialPlan.id);
        onClose();
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95">
        <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-white dark:bg-slate-800">
          <div>
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300">
              {isEditing ? 'Edit Workspace Tier' : 'Create New Workspace Tier'}
            </span>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white mt-1">{isEditing ? tierName || 'Edit Tier' : 'New Workspace Tier'}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          <PlanFormFields
            tierName={tierName}
            onTierNameChange={setTierName}
            planId={planId}
            onPlanIdChange={val => {
              setIsCustomPlanId(true);
              setPlanId(val);
            }}
            isEditing={isEditing}
            price={price}
            onPriceChange={setPrice}
            currency={currency}
            onCurrencyChange={setCurrency}
          />

          <PlanFeaturesList
            features={features}
            onAddFeature={text => setFeatures(prev => [...prev, text || ''])}
            onUpdateFeature={(idx, val) => setFeatures(prev => { const n = [...prev]; n[idx] = val; return n; })}
            onRemoveFeature={idx => setFeatures(prev => prev.filter((_, i) => i !== idx))}
          />

          <div className="pt-2 border-t border-slate-100 dark:border-slate-700 grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
            <div>
              <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider block mb-1">Description (Optional)</label>
              <input
                type="text"
                placeholder="Short summary for this tier"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Highlight as Popular</p>
                <p className="text-[10px] text-slate-400">Shows "Most Popular" tag on card</p>
              </div>
              <button
                type="button"
                onClick={() => setIsPopular(!isPopular)}
                className={`w-11 h-6 rounded-full relative p-0.5 transition-colors ${isPopular ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-700'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-xs transition-transform ${isPopular ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between gap-3">
            {isEditing && onDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting || isSaving}
                className="px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-xs font-bold transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {isDeleting ? 'Deleting...' : 'Delete Tier'}
              </button>
            ) : <div />}
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-xl text-xs font-bold transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={isSaving || isDeleting} className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-colors shadow-sm disabled:opacity-50">
                {isSaving ? 'Saving...' : isEditing ? 'Save Tier' : 'Create Tier'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanModal;
